'use client';

import { getProcedureHtml } from '@/domain/qualityAssurance/api';
import { type QualityAssuranceProcedure } from '@/domain/qualityAssurance/types';
import { handleException } from '@/utils/exception';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { BlockUI } from 'primereact/blockui';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Dialog } from 'primereact/dialog';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import styles from './ProcedureDetailDialog.module.scss';

interface ProcedureDetailDialogProps {
  selectedProcedure: QualityAssuranceProcedure;
  onClose: () => void;
}

const ProcedureDetailDialog = ({ selectedProcedure, onClose }: ProcedureDetailDialogProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getProcedureHtml(selectedProcedure.idQualityAssuranceProcedure)
      .then((html) => {
        if (iframeRef.current?.contentDocument) {
          iframeRef.current.contentDocument.body.innerHTML = html;
        }
      })
      .catch((err) => {
        toast.error(handleException(err, `Error downloading procedure: `));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedProcedure]);

  const renderFooter = () => (
    <Button variant="outlined" startIcon={<CloseOutlined />} onClick={onClose}>Close</Button>
  );

  return (
    <Dialog
      className={styles.dialog}
      modal
      maximizable
      visible
      header={selectedProcedure.name}
      blockScroll
      footer={renderFooter}
      onHide={onClose}
    >
      <BlockUI
        fullScreen
        blocked={isLoading}
        template={<i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }} />}
      >
        <div style={{ width: '100%', height: '100%' }}>
          <iframe
            ref={iframeRef}
            title={`${selectedProcedure.name}`}
            id={`${selectedProcedure.idQualityAssuranceProcedure}`}
            name={`${selectedProcedure.name}`}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            style={{ width: '100%', height: '100%' }}
            allowFullScreen
          />
        </div>
      </BlockUI>
    </Dialog>
  );
};

export default ProcedureDetailDialog;
